<template>
  <div>
    <span>
      <button class="btn btn-sm btn-label"
        @click.prevent="showEditor"
        v-for="labelInfo in selectedLabels"
        :key="labelInfo.id"
        :style="{color: textColourForBackground(labelInfo.colour), 'background-color': labelInfo.colour}">
        <i class="fal fa-tag mr-1" v-if="labelInfo.isFixed"></i>
        {{(labelInfo.name+'').trim() != '' ? labelInfo.name : '&nbsp;'}}
      </button>
    </span>
    <popper
      ref="popperPopover"
      @show="editorDisplayed=false"
      @hide="popoverHidden"
      trigger="click"
      :preventDefault="true"
      :stopPropagation="true"
      :append-to-body="false"
      :options="{
        placement: 'right',
        modifiers: { offset: { offset: '0,10px' } },
      }">
      <div class="popper label-editor-popover">
        <div>
          <div class="label-editor-main" :style="{display: !editorDisplayed ? 'block' : 'none'}">
            <div class="label-editor-header">
              <div class="col-sm-2"> </div>
              <span class="col-sm-8 text-center">{{popupName}}</span>
              <div class="col-sm-2 text-right">
                <button class="btn btn-sm label-editor-header-button" @click.prevent="hideEditor">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div ref="labelEditorListContainer" class="label-editor-list-scrollable">
              <ul class="label-editor-list">
                <li v-for="label in customLabelList" :key="label.id">
                  <span
                    class="label-editor-slot"
                    :style="{color: textColourForBackground(label.colour), 'background-color': label.colour}"
                    @click.prevent="toggleLabel(label)">
                    {{(label.name+'').trim() != '' ? label.name : '&nbsp;'}}
                    <span class="label-editor-slot-tick" v-if="isLabelSelected(label.id)"><i class="fas fa-check"></i></span>
                  </span>
                  <a class="label-editor-slot-edit" @click.prevent="showLabelEdit(label)" v-if="editable && userHasUpdatePermission(PermissionTypes.CorePermissionTypes.label)"><i class="far fa-pencil"> </i></a>
                </li>
                <li v-if="fixedLabelList.length > 0" class="text-muted text-left mt-2">
                  System Labels
                </li>
                <li v-for="label in fixedLabelList" :key="label.id">
                  <span
                    class="label-editor-slot"
                    :style="{color: textColourForBackground(label.colour), 'background-color': label.colour}"
                    @click.prevent="toggleLabel(label)">
                    <i class="fal fa-tag mr-1"></i>
                    {{(label.name+'').trim() != '' ? label.name : '&nbsp;'}}
                    <span class="label-editor-slot-tick" v-if="isLabelSelected(label.id)"><i class="fas fa-check"></i></span>
                  </span>
                  <a class="label-editor-slot-edit" @click.prevent="showLabelEdit(label)" v-if="editable && userHasUpdatePermission(PermissionTypes.CorePermissionTypes.label)"><i class="far fa-pencil"> </i></a>
                </li>
              </ul>
            </div>
            <div class="label-editor-options">
              <div class="text-left">
                <FormButton class="btn btn-xs btn-primary mr-2" @click="showLabelCreate" v-if="editable && userHasCreatePermission(PermissionTypes.CorePermissionTypes.label)"><i class="far fa-plus"></i> Add Label</FormButton>
                <FormButton class="btn btn-xs btn-warning" v-if="editable && fixedLabelList.length > 0 && userHasUpdatePermission(PermissionTypes.CorePermissionTypes.label)" @click="restoreLabels">Restore System Labels</FormButton>
              </div>
            </div>
          </div>
          <div :style="{display: editorDisplayed ? 'block' : 'none'}">
            <div class="label-editor-header">
              <div class="col-sm-2"><a class="label-editor-back" @click.prevent="hideLabelEdit">&lt; </a></div>
              <span class="col-sm-8 text-center">Label Editor</span>
              <div class="col-sm-2 text-right">
                <button class="btn btn-sm label-editor-header-button" @click.prevent="hideEditor">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div class="label-editor-edit-box" v-if="editingLabel">
              <div><strong>Name</strong></div>
              <input class="form-control" placeholder="Name" v-model="editingLabel.name" v-on:change="onLabelNameChanged" />
              <br />
              <div><strong>Colour</strong></div>
              <div class="label-editor-palette-list">
                <template v-for="(colour, index) in labelPalette">
                  <span
                    :key="index"
                    :class="['label-editor-palette-item']"
                    :style="{color: textColourForBackground(colour), 'background-color': colour}"
                    @click.prevent="updateLabelColour(colour)">
                    <i class="fas fa-check" v-if="isColourMatch(editingLabel.colour, colour)"></i>
                  </span>
                  <div v-if="index == 15" :key="`spacer-${index}`" class="label-editor-palette-seperator">&nbsp;</div>
                </template>
              </div>
              <div v-if="editingLabel.isFixed"><strong>System Label</strong></div>
              <div class="label-editor-fixed-description mt-1" v-if="editingLabel.isFixed">
                {{editingLabel.fixedDescription}}
              </div>
              <div class="label-editor-options" v-if="!editingLabel.isFixed && userHasDeletePermission(PermissionTypes.CorePermissionTypes.label)">
                <FormButton class="btn btn-xs btn-danger float-right" @click="deleteLabel(editingLabel.id)">Delete Label</FormButton>
              </div>
            </div>
            <div v-if="creatingLabel">
              <div class="label-editor-edit-box pb-0">
                <div><strong>Name</strong></div>
                <input class="form-control" placeholder="Name" v-model="creatingLabel.name" />
                <br />
                <div><strong>Colour</strong></div>
                <div class="label-editor-palette-list">
                  <template v-for="(colour, index) in labelPalette">
                  <span
                    :key="index"
                    :class="['label-editor-palette-item']"
                    :style="{color: textColourForBackground(colour), 'background-color': colour}"
                    @click.prevent="updateLabelColour(colour)">
                    <i class="fas fa-check" v-if="isColourMatch(creatingLabel.colour, colour)"></i>
                  </span>
                  <div v-if="index == 15" :key="`spacer-${index}`" class="label-editor-palette-seperator">&nbsp;</div>
                </template>
                </div>
              </div>
              <div class="label-editor-options text-left">
                <FormButton class="btn btn-xs btn-primary" @click="createNewLabel(creatingLabel)" :loading="createLabelLoading">Create Label</FormButton>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button slot="reference" class="btn btn-sm">{{buttonLabel}}</button>
    </popper>
    <div :class="{'form-invalid-feedback': true, 'is-invalid': true}" v-if="hasError()">
      {{getError()}}
    </div>
    <span v-if="!hasError() && note" class="form-field-note">{{note}}</span>
  </div>
</template>

<style lang="scss">
.label-editor-popover {
  padding: 0px 0px 1rem 0px;
  min-width: 325px;
  width: 325px;
  border-radius: $layout-minor-border-radius;
}
.label-editor-list-scrollable {
  max-height: 50vh;
  overflow-x: hidden;
  overflow-y: auto;
}
.label-editor-header {
  min-height: 30px;
  border-bottom: 1px solid #e7eaec;
  line-height: 30px;
  vertical-align: middle;
  font-size: 15px;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
}
.label-editor-header-button {
    font-size: 1.0rem;
    font-weight: 600;
    line-height: 1.1;
    color: #a0a0a0;
    background-color: transparent;
}
.popper {
  color: inherit;
}
.btn-label {
  color: #ffffff;
  margin-right: 4px;
}
ul.label-editor-list {
  padding: 12px 12px 0px 12px;
  list-style-type: none;
}
.label-editor-list > li {
  height: 36px;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
}
.label-editor-slot {
  width: 250px;
  height: 32px;
  padding: 6px;
  text-align: left;
  color: #ffffff;
  border-radius: 10px;
  font-weight: 500;
  display: inline-block;
  cursor: pointer;
}
.label-editor-slot:hover {
  box-shadow: 0px 3px 0px 0px rgba(0, 0, 0, 0.2);
}
.label-editor-slot:active {
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.2);
  transform: translateY(1px);
}
.label-editor-options {
  border-top: 1px solid #e7eaec;
  padding: 12px 12px 0px 12px;
}
.label-editor-fixed-description {
  font-size: 13px;
}

.label-editor-slot-edit {
  height: 32px;
  padding: 6px;
  margin-left: 10px;
}
.label-editor-slot-edit:hover {
  border-radius: 10px;
  background-color: #efefef;
}
.label-editor-slot-edit > i {
  color: #909090;
  width: 22px;
}
.label-editor-slot-tick {
  float: right;
  margin-right: 8px;
}
.label-editor-slot-tick > i {
  color: #ffffff;
  text-shadow: 1px 1px 0 rgba(0,0,0,0.33);
}

/* Edit Label Item */
.label-editor-edit-box {
  text-align: left;
  padding: 12px;
}
.label-editor-palette-list {
  padding-bottom: 16px;
}
.label-editor-palette-list::after {
    clear: both;
    content: "";
    display: table;
}

.label-editor-palette-item {
  display: block;
  float: left;
  min-width: 31px;
  height: 28px;
  border-radius: 4px;
  margin: 0px 6px 6px 0px;
  cursor: pointer;
  text-align: center;
  color: #ffffff;
}
.label-editor-palette-item > i {
  display: inline-block;
  top: 4px;
  position: relative;
}
.label-editor-palette-seperator {
  display: block;
  clear: both;
  line-height: 0.6rem;
}
.label-editor-back {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 30px;
  height: 30px;
  font-weight: bold;  
}
</style>

<script>
import Popper from 'vue-popperjs';
import 'vue-popperjs/dist/vue-popper.css';
import { setTimeout } from 'timers';
import formField from '@/mixins/formField'
import colourMixin from '@/mixins/colour'

export default {
  name: "FormLabelTag",
  mixins: [formField, colourMixin],
  props: {
    buttonLabel: {
      type: String,
      default: '+'
    },
    type: {
      type: String,
      default: 'text'
    },
    editable: {
      type: Boolean,
      default: true
    },
    editorOnly: {
      type: Boolean,
      default: false
    },
    singleLabelMode: {
      type: Boolean,
      default: false
    },
    popupName: {
      type: String,
      default: 'Labels'
    },
    labelType: String,
    value: Array,
    placeholder: String,
    field: String,
    note: String
  },
  components: {
    'popper': Popper
  },
  data() {
    return {
      createLabelLoading: false,
      editorDisplayed: false,
      editingLabel: null,
      creatingLabel: null,
      selectedLabels: this.value
    }
  },
  watch: {
    value: {
      immediate: true,
      handler: function (newVal, oldVal) {
        this.selectedLabels = this.value
      },
      deep: true
    }
  },
  computed: {
    labelPalette() {
      return [
        '#F3112B', '#FA5A2B', '#FD8A2E', '#FEF86B', '#7EC342', '#14B045', '#009E49', '#00A192',
        '#00ABED', '#0064B1', '#00499C', '#292786', '#5E2184', '#8C1B84', '#F40082', '#F60053',
        '#FEFEFE', '#D1D1D1', '#AEAEAE', '#838383', '#6A6A6A', '#585858', '#2F2F2F', '#0B0B0B',
        '#FF001D', '#FEF937', '#00FF2F', '#00FFFD', '#1A00F7', '#FF00F8', '#907D6A', '#845736',
      ]
    },
    labelList() {
      return this.$store.getters['label/getLabelsByType'](this.labelType)
    },
    customLabelList() {
      return this.labelList.filter(label => !label.isFixed)
    },
    fixedLabelList() {
      return this.labelList.filter(label => label.isFixed)
    }
  },
  mounted() {
    if(this.labelType == null) {
      return
    }

    this.$apiService.coreService.getLabelsForType(this.labelType)
      .then(response => {
        this.$store.commit({
          type: 'label/updateLabels',
          labelType: this.labelType,
          labelList: response.data
        })
      })
  },
  methods: {
    hasError() {
      return this.getFieldError(this.$props.field).length > 0
    },
    getError() {
      return this.getFieldError(this.$props.field)
    },
    showEditor(evt) {
      this.$nextTick(() => {
        this.$refs.popperPopover.doToggle(evt)
        if(this.$refs.labelEditorListContainer)
          this.$refs.labelEditorListContainer.scrollTo(0, 0)
      })
    },
    hideEditor() {
      this.$nextTick(() => {
        this.$refs.popperPopover.doClose()
        if(this.$refs.labelEditorListContainer)
          this.$refs.labelEditorListContainer.scrollTo(0, 0)
      })
    },
    showLabelEdit(label) {
      this.editorDisplayed = true
      this.editingLabel = label
      this.creatingLabel = null

      this.$nextTick(() => {
        this.$refs.popperPopover.updatePopper()
        if(this.$refs.labelEditorListContainer)
          this.$refs.labelEditorListContainer.scrollTo(0, 0)
      })
    },
    hideLabelEdit() {
      this.editorDisplayed = false

      this.$nextTick(() => {
        this.$refs.popperPopover.updatePopper()
        if(this.$refs.labelEditorListContainer)
          this.$refs.labelEditorListContainer.scrollTo(0, 0)
      })
    },
    showLabelCreate() {
      this.editorDisplayed = true
      this.editingLabel = null
      this.creatingLabel = {
        name: '',
        colour: this.labelPalette[0]
      }

      this.$nextTick(() => {
        this.$refs.popperPopover.updatePopper()
        if(this.$refs.labelEditorListContainer)
          this.$refs.labelEditorListContainer.scrollTo(0, 0)
      })
    },
    onLabelNameChanged() {
      // Notify the server
      this.$apiService.coreService.updateLabel(this.editingLabel.id, { name: this.editingLabel.name })

      var selected = this.selectedLabels.find(item => item.id == this.editingLabel.id)
      if(selected) {
        selected.name = this.editingLabel.name
        this.$emit('input', this.selectedLabels)
      }
    },
    toggleLabel(label) {
      if(this.editorOnly)
        return

      if(this.selectedLabels == null)
        this.selectedLabels = []

      if(this.isLabelSelected(label.id)) {
        this.selectedLabels = this.selectedLabels.filter((item) => { return item.id != label.id })
        this.$emit('input', this.selectedLabels)
      } else {
        // If limited to a single label, remove the other selection first
        if(this.singleLabelMode) {
          this.selectedLabels = []
        }

        this.selectedLabels.push(label)
        this.$emit('input', this.selectedLabels)
      }
    },
    updateLabelColour(colour) {
      if(this.editingLabel) {
        this.labelList.forEach((label) => {
          // Edit mode
          if(label.id == this.editingLabel.id) {
            label.colour = colour
            this.editingLabel.colour = colour

            // Notify the server
            this.$apiService.coreService.updateLabel(label.id, { colour: label.colour })
            .then(() => {
              this.$emit('labelUpdated', { id: label.id })
            })

            // Update selected labels
            var selected = this.selectedLabels.find(item => item.id == this.editingLabel.id)
            if(selected) {
              selected.colour = colour

              this.$emit('input', this.selectedLabels)
            }
          }
        })
      }

      // Create mode
      if(this.creatingLabel) {
        this.creatingLabel.colour = colour
      }
    },
    labelForId(id) {
      return this.labelList.filter((item) => { return item.id == id })[0]
    },
    labelNameForId(id) {
      var label = this.labelForId(id)
      return label ? label.name : ''
    },
    isLabelSelected(id) {
      if(this.selectedLabels == null)
        return false

      return this.selectedLabels.filter((item) => { return item.id == id }).length > 0
    },
    isColourMatch(colour1, colour2) {
      return (colour1 || '').trim().toLowerCase() == (colour2 || '').trim().toLowerCase()
    },
    createNewLabel(labelData) {
      this.createLabelLoading = true

      var data = {
        type: this.labelType,
        name: labelData.name,
        colour: labelData.colour
      }

      this.$apiService.coreService.createLabel(data)
      .then(newLabelData => {

        // Refresh label list
        this.$apiService.coreService.getLabelsForType(this.labelType)
        .then(response => {
          this.$store.commit({
            type: 'label/updateLabels',
            labelType: this.labelType,
            labelList: response.data
          })

          this.hideLabelEdit()

          // Select the new label
          this.toggleLabel({
            id: newLabelData.data.id,
            name: newLabelData.data.name,
            colour: newLabelData.data.colour
          })
        })
        .final(() => {
          this.createLabelLoading = false
        })

        this.$emit('labelUpdated', { id: newLabelData.data.id })
      })
      .catch(() => {
        this.createLabelLoading = false
      })
    },
    deleteLabel(id) {
      this.$refs.popperPopover.doClose()
      var curLabel = this.editingLabel
      this.editingLabel = null

      this.confirmDeleteMessage('Delete Label?', 'Are you sure you want to delete this label? The label will be removed from all records linked to this label')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLabel(id).then(() => {

            // Remove label from the list
            if(this.isLabelSelected(id)) {
              this.selectedLabels = this.selectedLabels.filter((item) => { return item.id != id })
              this.$emit('input', this.selectedLabels)
            }
            
            // Refresh label list
            this.$apiService.coreService.getLabelsForType(this.labelType)
            .then(response => {
              this.$store.commit({
                type: 'label/updateLabels',
                labelType: this.labelType,
                labelList: response.data
              })
            })

            this.$emit('labelUpdated', { id: id })
          })

          curLabel = null
        }

        // HACK: Re-show the popover as the dialog box hides it
        this.$nextTick(() => {
          // Terrible hack, but modal/popper don't behave together with the document click events
          setTimeout(() => {
            this.$refs.popperPopover.doShow()

            if(curLabel) {
              this.$nextTick(() => {
                this.showLabelEdit(curLabel)
              })
            }
          }, 250)
        })
        
      })
    },
    // Restore labels
    // Only available for fixed types (like status labels)
    restoreLabels() {
      this.$refs.popperPopover.doClose()

      this.confirmDeleteMessage('Restore Label Type?', 'Are you sure you want to restore the label names/colours for this type? ')
      .then(success => {
        if(success) {
          this.$apiService.coreService.restoreLabelsForType(this.labelType).then(() => {
            
            // Refresh label list
            this.$apiService.coreService.getLabelsForType(this.labelType)
            .then(response => {
              this.$store.commit({
                type: 'label/updateLabels',
                labelType: this.labelType,
                labelList: response.data
              })
            })
          })
        }

        // HACK: Re-show the popover as the dialog box hides it
        this.$nextTick(() => {
          // Terrible hack, but modal/popper don't behave together with the document click events
          setTimeout(() => {
            this.$refs.popperPopover.doShow()
          }, 250)
        })
        
      })
    },
    popoverHidden() {
      this.$emit('popoverHidden')
    }
  }
};
</script>